import { MRT_Row, type MRT_ColumnDef } from "material-react-table";
import { useMemo } from "react";

import { AllowanceBaseModel } from "@/features/allowance/types/allowanceModels";
import { OverviewItemsModel } from "@/shared/components/baselineOverview/model/baselineOverviewModels";
import Footer from "@/shared/components/FooterForColumns";
import { RNumberInputLabel } from "@/shared/components/inputs";
import RTextInputLabel from "@/shared/components/inputs/textInputs/RTextInputLabel";
import { LONG_DESCRIPTION, LONG_NAMES } from "@/shared/constants";

type HandleSaveCellType = (
  allowanceToUpdate: AllowanceBaseModel,
  name: string,
  value: number | string
) => void;

type AllowanceTableColumnsProps = {
  handleSaveCell: HandleSaveCellType;
  overviewItems: OverviewItemsModel[];
};

const AllowanceTableColumns = ({
  handleSaveCell,
  overviewItems,
}: AllowanceTableColumnsProps) =>
  useMemo<MRT_ColumnDef<AllowanceBaseModel>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        minSize: 90,
        size: 150,
        maxSize: 250,
        muiTableBodyCellProps: {
          align: "left",
        },
        Cell: ({ row }: { row: MRT_Row<AllowanceBaseModel> }) => (
          <RTextInputLabel
            value={row.original.name}
            onInputChange={(newValue) => {
              void handleSaveCell(row.original, "name", newValue);
            }}
            maxLength={LONG_NAMES} // Adjust as needed
            typographyProps={{ fontWeight: "bold" }}
            multiline={false}
          />
        ),
        enableEditing: true,
      },
      {
        accessorKey: "description",
        header: "Description",
        minSize: 90,
        size: 200,
        maxSize: 300,
        muiTableBodyCellProps: {
          align: "left",
        },
        Cell: ({ row }: { row: MRT_Row<AllowanceBaseModel> }) => (
          <RTextInputLabel
            value={row.original.description}
            onInputChange={(newValue) => {
              void handleSaveCell(row.original, "description", newValue);
            }}
            maxLength={LONG_DESCRIPTION} // Adjust as needed
            multiline={true}
          />
        ),
        enableEditing: true,
      },
      {
        accessorKey: "cost",
        header: "Cost",
        minSize: 75,
        size: 90,
        maxSize: 150,
        muiTableBodyCellProps: {
          align: "right",
        },
        Cell: ({ row }: { row: MRT_Row<AllowanceBaseModel> }) => (
          <RNumberInputLabel
            value={row.original.cost ?? 0}
            category={"currency"}
            onInputChange={(newValue) => {
              void handleSaveCell(row.original, "cost", newValue);
            }}
            decimalScaleInput={6}
            decimalScaleLabel={2}
            allowNegative
          />
        ),
        aggregationFn: "sum",
        enableEditing: true,
        Footer: () => (
          <Footer id="allowanceCost" items={overviewItems} category="currency" />
        ),
      },
      {
        accessorKey: "duration",
        header: "Duration",
        minSize: 75,
        size: 90,
        maxSize: 150,
        muiTableBodyCellProps: {
          align: "right",
        },
        Cell: ({ row }: { row: MRT_Row<AllowanceBaseModel> }) => (
          <RNumberInputLabel
            value={row.original.duration ?? 0}
            category={"duration"}
            onInputChange={(newValue) => {
              void handleSaveCell(row.original, "duration", newValue);
            }}
            decimalScaleInput={6}
            decimalScaleLabel={2}
            allowNegative
          />
        ),
        aggregationFn: "sum",
        enableEditing: true,
        Footer: () => (
          <Footer id="allowanceDuration" items={overviewItems} category="duration" />
        ),
      },
      {
        accessorKey: "emissions",
        header: "Emissions",
        minSize: 75,
        size: 90,
        maxSize: 150,
        muiTableBodyCellProps: {
          align: "right",
        },
        Cell: ({ row }: { row: MRT_Row<AllowanceBaseModel> }) => (
          <RNumberInputLabel
            value={row.original.emissions ?? 0}
            category={"emissions"}
            onInputChange={(newValue) => {
              void handleSaveCell(row.original, "emissions", newValue);
            }}
            decimalScaleInput={6}
            decimalScaleLabel={2}
            allowNegative
          />
        ),
        aggregationFn: "sum",
        enableEditing: true,
        Footer: () => (
          <Footer id="allowanceEmissions" items={overviewItems} category="emissions" />
        ),
      },
    ],
    [handleSaveCell, overviewItems]
  );

export default AllowanceTableColumns;
